import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { BackButton, FullPageSpinner } from 'components'
import { Redirect, useHistory, useParams, Link } from 'react-router-dom'
import { formatUrl } from 'utils'
import {
  UploadPlanAgreementModal,
  DownloadNotAvailableModal,
  ReInitializeDocusignModal,
} from '../components'
import { Path } from 'config/portal'
import {
  ServicesAgreementStatus,
  CombinedServiceAgreementStatus,
} from 'config/automatic-rollovers'
import { flashErrorMessage } from 'redux-flash'
import * as actions from '../actions'
import NotificationsAgreementParticipants from './NotificationsAgreementParticipants'
import { ProductAgreementType } from 'config/products'
import { Icon as NotificationIcon } from 'images/product-icons/notification-services-new.svg'
import { TOAST_ERROR_CONTACT_SUPPORT } from 'config/notifications'

const propTypes = {
  fetchServicesAgreementDetails: PropTypes.func.isRequired,
  servicesAgreement: Types.servicesAgreementShow,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  clearServicesAgreementDetails: PropTypes.func.isRequired,
  fetchAvailableInitialInvestments: PropTypes.func.isRequired,
  availableInitialInvestments: PropTypes.arrayOf(
    Types.availableInitialInvestment
  ),
  shouldTriggerServicesAgreementDetailsFetch: PropTypes.bool.isRequired,
  setShouldTriggerServicesAgreementDetailsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllServicesAgreementsFetch: PropTypes.func.isRequired,
  setShouldTriggerAllBDPlanAgreementsFetch: PropTypes.func.isRequired,
  agreementsType: PropTypes.string.isRequired,
}

const defaultProps = {
  servicesAgreement: null,
  availableInitialInvestments: null,
}

const PARTICIPANTS_INITIAL_FILTERS = {
  status: [],
}

function NotificationsAgreementDetails({
  fetchServicesAgreementDetails,
  servicesAgreement,
  flashErrorMessageHandler,
  clearServicesAgreementDetails,
  fetchAvailableInitialInvestments,
  availableInitialInvestments,
  shouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch,
  agreementsType,
}) {
  const history = useHistory()
  const [errorExists, setErrorExists] = useState(false)
  const [showDownloadNotAvailableModal, setShowDownloadNotAvailableModal] =
    useState(false)
  const [showResendModal, setShowResendModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const { servicesAgreementId } = useParams()
  const [participantsFilters, setParticipantsFilters] = useState(
    PARTICIPANTS_INITIAL_FILTERS
  )
  const [participantsSearch, setParticipantsSearch] = useState('')

  useEffect(() => {
    return () => {
      clearServicesAgreementDetails()
      setShouldTriggerServicesAgreementDetailsFetch(true)
    }
  }, [])

  useEffect(() => {
    async function getInitialInvestments() {
      try {
        await fetchAvailableInitialInvestments()
      } catch {
        setErrorExists(true)
      }
    }
    if (!availableInitialInvestments) getInitialInvestments()
  }, [availableInitialInvestments])

  useEffect(() => {
    async function getPlanAgreementDetails() {
      try {
        await fetchServicesAgreementDetails(servicesAgreementId)
        setShouldTriggerServicesAgreementDetailsFetch(false)
      } catch {
        setErrorExists(true)
      }
    }
    if (shouldTriggerServicesAgreementDetailsFetch) getPlanAgreementDetails()
  }, [shouldTriggerServicesAgreementDetailsFetch])

  const isLoading =
    (!servicesAgreement || !availableInitialInvestments) && !errorExists

  if (isLoading) return <FullPageSpinner />
  if (errorExists) {
    flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
    return <Redirect to={Path.SERVICES_AGREEMENTS} />
  }

  const { name, portalAgreementStatus } = servicesAgreement

  const fieldMappings = [
    { field: 'participantName', displayName: 'Participant name' },
    { field: 'accountType', displayName: 'Response period expiration' },
    { field: 'status', displayName: 'Order number' },
    { field: 'createdDate', displayName: 'Delivery status' },
    {
      field: 'initialInvestmentAmount',
      displayName: 'Last updated',
    },
  ]

  return (
    <div className="notification-agreement-details-view">
      <div className="header-bar">
        <BackButton
          link={(location) => {
            return {
              pathname: Path.NOTIFICATIONS_HISTORY,
              search: location.state?.search,
            }
          }}
        >
          Back to all plans
        </BackButton>
        <div className="header-bar-content">
          <div className="notifiaction-heading">
            <div className="notification-icon-component">
              <NotificationIcon />
              <h1>{name}</h1>
            </div>
            <div className="agreement-link">
              <Link
                to={(location) => {
                  return {
                    pathname: formatUrl(
                      `${Path.SERVICES_AGREEMENTS}/${servicesAgreementId}`
                    ),
                    state: { search: location.search },
                  }
                }}
                className="primary-base"
                id="plan-name-link-bd"
              >
                View services agreement
              </Link>
            </div>
          </div>
          <div className="services-agreements-actions">
            {(portalAgreementStatus === ServicesAgreementStatus.COMPLETED ||
              portalAgreementStatus ===
                CombinedServiceAgreementStatus.COMBINED_COMPLETED) && (
              <>
                <button
                  type="button"
                  onClick={() =>
                    history.push({
                      pathname: Path.UPLOAD_FILE_NOTIFICATION_SERVICES,
                      state: {
                        hasConfirmed: true,
                      },
                    })
                  }
                  className="button-primary"
                >
                  {'Start notification order'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <NotificationsAgreementParticipants
        servicesAgreementId={servicesAgreementId}
        baseFilters={PARTICIPANTS_INITIAL_FILTERS}
        filters={participantsFilters}
        handleFilter={(filters) => setParticipantsFilters(filters)}
        search={participantsSearch}
        handleSearch={(q) => setParticipantsSearch(q)}
        fieldMappings={fieldMappings}
      />
      {showUploadModal && (
        <UploadPlanAgreementModal
          onClose={() => setShowUploadModal(false)}
          servicesAgreement={servicesAgreement}
          onSubmitSuccess={() => {
            setShowUploadModal(false)
            setShouldTriggerServicesAgreementDetailsFetch(true)
            setShouldTriggerAllServicesAgreementsFetch(true)
            setShouldTriggerAllBDPlanAgreementsFetch(true)
          }}
          agreementsType={agreementsType}
        />
      )}
      {showDownloadNotAvailableModal && (
        <DownloadNotAvailableModal
          onClose={() => setShowDownloadNotAvailableModal(false)}
        />
      )}
      {showResendModal && (
        <ReInitializeDocusignModal
          agreementId={servicesAgreementId}
          onClose={() => setShowResendModal(false)}
          productAgreementType={ProductAgreementType.ARO}
          status={portalAgreementStatus}
        />
      )}
    </div>
  )
}

function mapStateToProps(state) {
  return {
    servicesAgreement: selectors.servicesAgreement(state),
    availableInitialInvestments: selectors.availableInitialInvestments(state),
    shouldTriggerServicesAgreementDetailsFetch:
      selectors.shouldTriggerServicesAgreementDetailsFetch(state),
  }
}

const mapDispatchToProps = {
  fetchServicesAgreementDetails: apiActions.fetchServicesAgreementDetails,
  flashErrorMessageHandler: flashErrorMessage,
  clearServicesAgreementDetails: actions.clearServicesAgreementDetails,
  fetchAvailableInitialInvestments: apiActions.fetchAvailableInitialInvestments,
  setShouldTriggerServicesAgreementDetailsFetch:
    actions.setShouldTriggerServicesAgreementDetailsFetch,
  setShouldTriggerAllServicesAgreementsFetch:
    actions.setShouldTriggerAllServicesAgreementsFetch,
  setShouldTriggerAllBDPlanAgreementsFetch:
    actions.setShouldTriggerAllBDPlanAgreementsFetch,
}

NotificationsAgreementDetails.propTypes = propTypes
NotificationsAgreementDetails.defaultProps = defaultProps

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  NotificationsAgreementDetails
)
